/**
 * Login Page
 *
 * 백엔드 인증(OIDC)을 사용한 로그인 페이지
 * - 이메일/비밀번호 로그인
 * - 3 테마(light / dark HMI / legacy) 토큰 기반 스타일
 * - 로그인된 사용자(도착 시점이든 로그인 직후든)를 원래 자리로 보내는 것은 라우터의 PublicOnlyRoute 가 한다
 */

import { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { useAuthStore } from '@/app/stores/authStore';
import { useThemeStore } from '@/app/stores/themeStore';

const LoginPage: React.FC = () => {
  const { loading, error, signInWithEmail, clearError, resetLoading } = useAuthStore();
  const theme = useThemeStore((s) => s.theme);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    resetLoading();
  }, [resetLoading]);

  const handleEmailLogin = async () => {
    // 진행 중 재제출 차단 — 스토어 signInWithEmail 에는 진행 중 가드가 없고, 비활성 제출 버튼은
    // 브라우저 암묵 제출(Enter)만 막는다. form.requestSubmit()(비밀번호 관리자 등)은 그 게이트를 지나지 않는다.
    if (loading) return;
    if (!email.trim() || !password.trim()) return;
    try {
      await signInWithEmail(email, password);
    } catch (err) {
      // 에러는 스토어에서 처리
    }
  };

  const isLight = theme === 'light';
  const isLegacy = theme === 'legacy';

  // Page background — 라이트 테마는 hero 그라디언트, 그 외는 캔버스 토큰
  const pageBackground = isLight
    ? 'linear-gradient(160deg, #0F2B4C 0%, #162d50 30%, #2563EB 100%)'
    : 'var(--bg-canvas)';

  // Card 표면 — 라이트는 글래스, dark/legacy는 패널 토큰
  const cardSx = isLight
    ? {
        background: 'rgba(255, 255, 255, 0.92)',
        backdropFilter: 'blur(16px)',
        border: '1px solid rgba(255, 255, 255, 0.6)',
        borderRadius: 4,
        boxShadow: '0 8px 32px rgba(15, 43, 76, 0.25)',
      }
    : {
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: isLegacy ? 0 : 'var(--radius-lg)',
        boxShadow: isLegacy ? 'var(--shadow-card)' : 'var(--shadow-panel)',
      };

  // 입력 필드 — 라벨/테두리/autofill 모두 토큰 기반
  const fieldSx = {
    '& .MuiInputLabel-root': {
      color: 'var(--text-secondary)',
      '&.Mui-focused': { color: 'var(--accent-primary)' },
    },
    '& .MuiOutlinedInput-root': {
      color: 'var(--text-primary)',
      backgroundColor: 'var(--bg-card)',
      '& fieldset': { borderColor: 'var(--border)' },
      '&:hover fieldset': { borderColor: 'var(--accent-primary)' },
      '&.Mui-focused fieldset': { borderColor: 'var(--accent-primary)' },
    },
    // Chrome/Safari autofill 배경이 input을 다크 블루로 덮으면서 라벨 가독성을 무너뜨리던 문제 차단
    '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
      WebkitBoxShadow: '0 0 0 100px var(--bg-card) inset',
      WebkitTextFillColor: 'var(--text-primary)',
      caretColor: 'var(--text-primary)',
      transition: 'background-color 5000s ease-in-out 0s',
    },
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: pageBackground,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* 라이트 테마 전용 hero glow — dark/legacy는 캔버스 단색을 그대로 살림 */}
      {isLight && (
        <>
          <Box
            sx={{
              position: 'absolute',
              top: -100,
              right: -100,
              width: 400,
              height: 400,
              borderRadius: '50%',
              background: 'rgba(147, 197, 253, 0.1)',
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -120,
              left: -80,
              width: 350,
              height: 350,
              borderRadius: '50%',
              background: 'rgba(37, 99, 235, 0.15)',
              pointerEvents: 'none',
            }}
          />
        </>
      )}

      <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 1 }}>
        {/* 진짜 <form> 이어야 브라우저가 **어느 입력 칸에서든** Enter 를 제출로 바꾼다(자동 채움 포함).
            전에는 비밀번호 칸의 onKeyDown 만 있어 이메일 칸의 Enter 가 아무 일도 하지 않았다. */}
        <Box
          component="form"
          noValidate
          aria-label="로그인"
          onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            void handleEmailLogin();
          }}
          sx={{ p: 5, ...cardSx }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: isLegacy ? 700 : 800,
              textAlign: 'center',
              mb: 0.5,
              color: isLight ? '#0F2B4C' : 'var(--text-strong)',
              letterSpacing: isLegacy ? '0.05em' : '-0.5px',
              fontFamily: 'var(--font-display)',
              textTransform: isLegacy ? 'uppercase' : 'none',
            }}
          >
            VSMR Editor
          </Typography>

          <Typography
            variant="body2"
            sx={{
              textAlign: 'center',
              mb: 4,
              color: 'var(--text-secondary)',
              fontSize: '0.875rem',
              fontFamily: 'var(--font-base)',
            }}
          >
            계정으로 로그인하세요
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="이메일"
            type="email"
            variant="outlined"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value.replace(/\s/g, ''));
              clearError();
            }}
            sx={{ mb: 2, ...fieldSx }}
            autoFocus
          />

          <TextField
            fullWidth
            label="비밀번호"
            type="password"
            variant="outlined"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value.replace(/\s/g, ''));
              clearError();
            }}
            sx={{ mb: 3, ...fieldSx }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading || !email.trim() || !password.trim()}
            sx={{
              py: 1.5,
              mb: 3,
              background: isLight ? '#2563EB' : 'var(--accent-primary)',
              color: 'var(--text-on-accent)',
              fontWeight: 700,
              fontSize: '0.95rem',
              borderRadius: isLegacy ? 0 : 2,
              boxShadow: isLight
                ? '0 4px 16px rgba(37,99,235,0.3)'
                : isLegacy
                ? 'var(--shadow-bevel)'
                : 'none',
              '&:hover': {
                background: isLight ? '#1d4ed8' : 'var(--accent-primary-hover)',
              },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : '로그인'}
          </Button>

          <Typography
            variant="body2"
            sx={{ textAlign: 'center', color: 'var(--text-secondary)' }}
          >
            계정이 없으신가요?{' '}
            <Link
              component={RouterLink}
              to="/signup"
              underline="hover"
              sx={{ color: 'var(--accent-primary)', fontWeight: 600 }}
            >
              회원가입
            </Link>
          </Typography>
        </Box>

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              background: isLight
                ? 'rgba(147,197,253,0.15)'
                : 'var(--bg-card-alt)',
              color: isLight ? '#93C5FD' : 'var(--text-muted)',
              border: isLight ? 'none' : '1px solid var(--border-subtle)',
              fontSize: '0.7rem',
              fontWeight: 600,
              letterSpacing: 'var(--label-letter)',
              textTransform: 'var(--label-transform)' as 'none' | 'uppercase',
              px: 1.5,
              py: 0.5,
              borderRadius: isLegacy ? 0 : 3,
              fontFamily: 'var(--font-base)',
            }}
          >
            VSMR Simulation Platform
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default LoginPage;
